import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, CreditCard, Truck, Ticket, Info } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../config';
import { useAuth } from '../contexts/AuthContext';

const playfairFont = "'Playfair Display', serif";

export const Checkout = () => {
  const navigate = useNavigate();
  const { } = useAuth();
  const [cart, setCart] = useState({ items: [] });
  const [ticketBalance, setTicketBalance] = useState(0);
  const [ticketsUsed, setTicketsUsed] = useState(0);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [formData, setFormData] = useState({
    shipping_name: '', shipping_address: '', shipping_phone: '',
    shipping_province: '', shipping_city: '',
  });

  useEffect(() => {
    (async () => {
      try {
        const [cartRes, ticketRes] = await Promise.all([
          axios.get(`${API}/cart`, { withCredentials: true }),
          axios.get(`${API}/tickets/balance`, { withCredentials: true })
        ]);
        if (!cartRes.data.items || cartRes.data.items.length === 0) {
          toast.error('Cart is empty');
          navigate('/cart');
          return;
        }
        setCart(cartRes.data);
        setTicketBalance(ticketRes.data.ticket_balance || 0);
      } catch {
        navigate('/cart');
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate]);

  const formatPrice = (price) => new Intl.NumberFormat('id-ID', {
    style: 'currency', currency: 'IDR', minimumFractionDigits: 0
  }).format(price);

  const subtotal = cart.items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  const maxTicketsForOrder = Math.floor(subtotal / 100);
  const maxTickets = Math.min(ticketBalance, maxTicketsForOrder);
  const discount = ticketsUsed * 100;
  const estimatedSubtotal = subtotal - discount; // shipping unknown yet

  const handleSubmit = async (e) => {
    e.preventDefault();
    setProcessing(true);
    try {
      const { data } = await axios.post(`${API}/orders/checkout`, {
        ...formData,
        tickets_used: ticketsUsed,
        // Shipping is quoted manually by admin AFTER order is placed.
        // Send zeros — backend parks the order in `awaiting_shipping_quote`.
        shipping_courier: '',
        shipping_service: '',
        shipping_cost: 0,
        payment_method: 'qris', // placeholder; customer will pick after admin quotes
      }, { withCredentials: true });
      toast.success('Pesanan dibuat. Menunggu ongkir dari admin.');
      navigate(`/orders/${data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Checkout failed');
    } finally {
      setProcessing(false);
    }
  };

  const inputStyle = { background: 'rgba(0,0,0,0.2)', border: '1px solid rgba(255,255,255,0.1)', color: '#F8FAF9' };
  const cardStyle = { background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)' };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#080C0A' }}>
        <div className="w-12 h-12 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: '#D4AF37', borderTopColor: 'transparent' }} />
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-12" style={{ background: '#080C0A', fontFamily: "'Outfit', sans-serif" }}>
      <header className="sticky top-0 z-40 backdrop-blur-2xl" style={{ background: 'rgba(8,12,10,0.9)', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center gap-4">
          <button onClick={() => navigate(-1)} className="hover:opacity-80" data-testid="back-btn">
            <ArrowLeft className="w-6 h-6" style={{ color: '#F8FAF9' }} />
          </button>
          <h1 className="text-2xl font-light flex items-center gap-3" style={{ fontFamily: playfairFont, color: '#F8FAF9' }}>
            <CreditCard className="w-6 h-6" style={{ color: '#D4AF37' }} /> Checkout
          </h1>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-6 py-8">
        <div className="grid md:grid-cols-2 gap-6">
          <div className="space-y-6">
            <div className="rounded-2xl p-6 backdrop-blur-xl" style={cardStyle}>
              <h2 className="text-xl font-light mb-6 flex items-center gap-2" style={{ fontFamily: playfairFont, color: '#F8FAF9' }}>
                <Truck className="w-5 h-5" style={{ color: '#D4AF37' }} /> Informasi Pengiriman
              </h2>
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="checkout-form" id="checkout-form">
                <div>
                  <label className="block text-sm mb-2" style={{ color: '#A3B8AC' }}>Nama Penerima</label>
                  <input type="text" value={formData.shipping_name} onChange={(e) => setFormData({ ...formData, shipping_name: e.target.value })} required className="w-full rounded-lg px-4 py-3 outline-none" style={inputStyle} placeholder="Nama lengkap" data-testid="shipping-name-input" />
                </div>
                <div>
                  <label className="block text-sm mb-2" style={{ color: '#A3B8AC' }}>Alamat Lengkap</label>
                  <textarea value={formData.shipping_address} onChange={(e) => setFormData({ ...formData, shipping_address: e.target.value })} required rows="3" className="w-full rounded-lg px-4 py-3 outline-none" style={inputStyle} placeholder="Jalan, RT/RW, Kelurahan, Kecamatan, Kota, Provinsi, Kode Pos" data-testid="shipping-address-input" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm mb-2" style={{ color: '#A3B8AC' }}>Provinsi</label>
                    <input type="text" value={formData.shipping_province} onChange={(e) => setFormData({ ...formData, shipping_province: e.target.value })} className="w-full rounded-lg px-4 py-3 outline-none" style={inputStyle} placeholder="Provinsi" data-testid="shipping-province-input" />
                  </div>
                  <div>
                    <label className="block text-sm mb-2" style={{ color: '#A3B8AC' }}>Kota/Kabupaten</label>
                    <input type="text" value={formData.shipping_city} onChange={(e) => setFormData({ ...formData, shipping_city: e.target.value })} className="w-full rounded-lg px-4 py-3 outline-none" style={inputStyle} placeholder="Kota/Kabupaten" data-testid="shipping-city-input" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm mb-2" style={{ color: '#A3B8AC' }}>Nomor HP</label>
                  <input type="tel" value={formData.shipping_phone} onChange={(e) => setFormData({ ...formData, shipping_phone: e.target.value })} required className="w-full rounded-lg px-4 py-3 outline-none" style={inputStyle} placeholder="08xxxxxxxxxx" data-testid="shipping-phone-input" />
                </div>
              </form>
            </div>

            {/* Manual Shipping Quote Notice */}
            <div className="rounded-2xl p-5 backdrop-blur-xl" style={{ background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.35)' }} data-testid="manual-shipping-notice">
              <div className="flex gap-3">
                <Info className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: '#F59E0B' }} />
                <div className="space-y-1">
                  <p className="font-semibold text-sm" style={{ color: '#F59E0B' }}>Ongkir akan dikonfirmasi oleh admin.</p>
                  <p className="text-xs" style={{ color: '#CFE0D5' }}>
                    Setelah pesanan dibuat, admin akan menghitung ongkos kirim manual berdasarkan alamat & berat barang. Anda akan mendapat notifikasi total pembayaran lalu dapat memilih metode pembayaran (QRIS / Transfer Bank / Coin).
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="rounded-2xl p-6 backdrop-blur-xl" style={cardStyle}>
            <h2 className="text-xl font-light mb-6" style={{ fontFamily: playfairFont, color: '#F8FAF9' }}>Ringkasan Pesanan</h2>
            <div className="space-y-3 mb-6">
              {cart.items.map((item) => (
                <div key={`${item.product_id}|${item.variant_key || ''}`} className="flex gap-3" data-testid={`order-item-${item.product_id}`}>
                  <img src={item.image_url} alt={item.name} className="w-16 h-16 object-cover rounded-xl" />
                  <div className="flex-1">
                    <p className="font-medium text-sm" style={{ color: '#F8FAF9' }}>{item.name}</p>
                    {item.variant_label && (
                      <p className="text-xs" style={{ color: '#D4AF37' }}>{item.variant_label}</p>
                    )}
                    <p className="text-sm" style={{ color: '#A3B8AC' }}>Qty: {item.quantity}</p>
                  </div>
                  <p className="font-semibold" style={{ color: '#D4AF37' }}>{formatPrice(item.price * item.quantity)}</p>
                </div>
              ))}
            </div>

            {/* Ticket Redemption */}
            {ticketBalance > 0 && maxTicketsForOrder > 0 && (
              <div className="rounded-xl p-4 mb-4" style={{ background: 'rgba(212,175,55,0.08)', border: '1px solid rgba(212,175,55,0.2)' }}>
                <div className="flex items-center gap-2 mb-2">
                  <Ticket className="w-4 h-4" style={{ color: '#D4AF37' }} />
                  <span className="text-sm font-medium" style={{ color: '#F8FAF9' }}>Pakai Tiket ({ticketBalance} tersedia)</span>
                </div>
                <p className="text-xs mb-3" style={{ color: '#A3B8AC' }}>1 tiket = Rp 100 off · maksimal {maxTickets} tiket</p>
                <div className="flex items-center gap-2">
                  <input
                    type="range"
                    min="0"
                    max={maxTickets}
                    value={ticketsUsed}
                    onChange={(e) => setTicketsUsed(parseInt(e.target.value))}
                    className="flex-1"
                    style={{ accentColor: '#D4AF37' }}
                    data-testid="tickets-slider"
                  />
                  <input
                    type="number"
                    min="0"
                    max={maxTickets}
                    value={ticketsUsed}
                    onChange={(e) => {
                      const v = Math.min(maxTickets, Math.max(0, parseInt(e.target.value) || 0));
                      setTicketsUsed(v);
                    }}
                    className="w-20 rounded-lg px-2 py-1 outline-none text-center"
                    style={inputStyle}
                    data-testid="tickets-input"
                  />
                </div>
              </div>
            )}

            <div className="space-y-2 pt-4" style={{ borderTop: '1px solid rgba(255,255,255,0.1)' }}>
              <div className="flex justify-between" style={{ color: '#A3B8AC' }}>
                <span>Subtotal</span>
                <span data-testid="order-subtotal">{formatPrice(subtotal)}</span>
              </div>
              <div className="flex justify-between" style={{ color: '#A3B8AC' }}>
                <span>Ongkir</span>
                <span style={{ color: '#F59E0B', fontStyle: 'italic' }} data-testid="order-shipping">Menunggu admin</span>
              </div>
              {discount > 0 && (
                <div className="flex justify-between" style={{ color: '#D4AF37' }} data-testid="order-discount">
                  <span>Tiket ({ticketsUsed})</span>
                  <span>-{formatPrice(discount)}</span>
                </div>
              )}
              <div className="flex justify-between text-lg font-bold pt-2" style={{ borderTop: '1px solid rgba(255,255,255,0.1)' }}>
                <span style={{ color: '#F8FAF9' }}>Subtotal (sebelum ongkir)</span>
                <span style={{ color: '#D4AF37', fontFamily: playfairFont }} data-testid="order-total">{formatPrice(estimatedSubtotal)}</span>
              </div>
              <p className="text-[11px] mt-1" style={{ color: '#A3B8AC' }}>
                Total akhir = subtotal + ongkir yang ditetapkan admin.
              </p>
            </div>

            <motion.button
              form="checkout-form"
              whileTap={{ scale: 0.98 }}
              type="submit"
              disabled={processing}
              className="w-full mt-6 py-4 rounded-full font-semibold disabled:opacity-50"
              style={{ background: '#D4AF37', color: '#080C0A' }}
              data-testid="place-order-btn"
            >
              {processing ? 'Memproses…' : 'Buat Pesanan (Tunggu Quote Ongkir)'}
            </motion.button>
            <p className="text-[11px] mt-2 text-center" style={{ color: '#A3B8AC' }}>
              Anda belum membayar di tahap ini.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
